import mapKeys from 'lodash/mapKeys'
import { v4 } from 'uuid'

import { isAbortError } from '@app/errors/AbortError'

import { captureException } from '@app/utils/errorReport/errorReport'

import { postTrackings } from '@app/packages/api/v2/actions/trackings'
import { asError } from '@app/packages/asError/asError'

import { callApi } from '@app/store/actions/api'
import type { Store } from '@app/store/store'

export interface TrackingsServiceInterface {
  sendEvent(id: string, data?: Record<string, any>): void
}

export class TrackingsService implements TrackingsServiceInterface {
  constructor(private store: Store) {}

  /**
   * Off the store's root context on purpose: an event describes something that already happened,
   * so the screen it came from must not be able to abort it, and its failure is not its caller's
   * problem. The dispatcher supplies the url, the headers and the token.
   */
  sendEvent(id: string, data: Record<string, any> = {}) {
    const request = { event_id: v4(), event_type: id, event_ts: Date.now(), event_data: serializeEventData(data) }
    try {
      void this.store.dispatch(callApi(postTrackings(request))).catch(reportSendFailure)
    } catch (e) {
      reportSendFailure(e)
    }
  }
}

/**
 * Produced flattened object:
 * So that:
 * `{
 *  type: "filter",
 *  filter: {
 *    rule_1: 3,
 *    rule_2: 5,
 *  },
 *  items: [1,2,3]
 * }`
 * becomes:
 * `{
 *  type: "filter",
 *  "filter.rule_1": 3,
 *  "filter.rule_2": 5,
 *  "items.0": 1,
 *  "items.1": 2,
 *  "items.2": 3
 * }`
 */
export function serializeEventData(data: Record<string, any>): Record<string, any> {
  const output: Record<string, any> = {}
  for (const [key, value] of Object.entries(data)) {
    if (Array.isArray(value)) {
      Object.assign(output, serializeEventData(Object.fromEntries(Object.entries(value).map(([index, item]) => [`${key}.${index}`, item]))))
    }
    if (typeof value === 'object' && value !== null) {
      let subvalues = serializeEventData(value)
      subvalues = mapKeys(subvalues, (_subValue, subKey) => {
        return `${key}.${subKey}`
      })
      Object.assign(output, subvalues)
    } else {
      output[key] = value
    }
  }

  return output
}

/** An event lost to a navigation is normal; anything else means telemetry is broken and should be seen. */
const reportSendFailure = (e: unknown) => {
  if (isAbortError(e)) return
  captureException(asError(e), { tags: { service: 'TrackingsService' } })
}
